import {createSelector} from 'reselect';

const notifications = state => state.departmentSettings.notifications;
const settings = state => state.departmentSettings.settings;
const userContext = state => state.common.userContext;
const selectedNotificationGuid = state => state.departmentSettings.selectedNotificationGuid;

const selectedNotification = createSelector(
  [notifications, selectedNotificationGuid],
  (list, notificationGuid) => {
    const selected = !list ? null : list.find(item => item.guid === notificationGuid);
    return !selected ? null : selected;
  },
);

const oitShortLabel = createSelector(
  [settings],
  (settings) => {
    return (settings && settings.rolesSettings && settings.rolesSettings.oit_short) || 'OIT'
  }
);

const ftoShortLabel = createSelector(
  [settings],
  (settings) => {
    return (settings && settings.rolesSettings && settings.rolesSettings.fto_short) || 'FTO'
  }
);

const ftocShortLabel = createSelector(
  [settings],
  (settings) => {
    return (settings && settings.rolesSettings && settings.rolesSettings.ftoc_short) || 'FTOC'
  }
);

const supShortLabel = createSelector(
  [settings],
  (settings) => {
    return (settings && settings.rolesSettings && settings.rolesSettings.sup_short) || 'SUP'
  }
);

const cstaffShortLabel = createSelector(
  [settings],
  (settings) => {
    return (settings && settings.rolesSettings && settings.rolesSettings.cstaff_short) || 'CSTAFF'
  }
);

const chiefShortLabel = createSelector(
  [settings],
  (settings) => {
    return (settings && settings.rolesSettings && settings.rolesSettings.chief_short) || 'CHIEF'
  }
);

const oitLabel = createSelector(
  [settings],
  (settings) => {
    return (settings && settings.rolesSettings && settings.rolesSettings.oit) || 'Officer In Training'
  }
);

const ftoLabel = createSelector(
  [settings],
  (settings) => {
    return (settings && settings.rolesSettings && settings.rolesSettings.fto) || 'Field Training Officer'
  }
);

const ftocLabel = createSelector(
  [settings],
  (settings) => {
    return (settings && settings.rolesSettings && settings.rolesSettings.ftoc) || 'FTO Coordinator'
  }
);

const supLabel = createSelector(
  [settings],
  (settings) => {
    return (settings && settings.rolesSettings && settings.rolesSettings.sup) || 'Supervisor'
  }
);

const cstaffLabel = createSelector(
  [settings],
  (settings) => {
    return (settings && settings.rolesSettings && settings.rolesSettings.cstaff) || 'Command Staff'
  }
);

const chiefLabel = createSelector(
  [settings],
  (settings) => {
    return (settings && settings.rolesSettings && settings.rolesSettings.chief) || 'Chief'
  }
);

const getLabel = (settings, role) => {
  return settings && settings.rolesSettings && settings.rolesSettings[role]
};

const currentRoleName = createSelector(
  [settings, userContext],
  (settings, userContext) => {
    return ({
      admin: 'App Admin',
      depadmin: 'Department Admin',
      cstaff: getLabel(settings, userContext.scope) || 'Command Staff',
      chief: getLabel(settings, userContext.scope) || 'Chief',
      ftoc: getLabel(settings, userContext.scope) || 'FTO Coordinator',
      fto: getLabel(settings, userContext.scope) || 'Field Training Officer',
      sup: getLabel(settings, userContext.scope) || 'Supervisor',
      oit: getLabel(settings, userContext.scope) || 'Officer In Training',
    }[userContext.scope])
  }
);

const availableTaskResponses = createSelector(
  [settings, userContext],
  (settings, userContext) => {
    let taskResponses = settings
      && settings.taskResponses
      || ['explained', 'demonstrated', 'practiced']

    if (userContext.scope === 'oit' && settings.oitTaskResponses && settings.oitTaskResponses.length) {
      taskResponses = settings.oitTaskResponses
    }
    return taskResponses;
  }
);

const canViewAnayitics = createSelector(
  [userContext, settings],
  (userContext, settings) => {
    return userContext.scope === 'ftoc'
      || userContext.scope === 'sup'
      || (settings && settings.analytics_roles && settings.analytics_roles.includes(userContext.scope))
  }
);

const canReviewOpenReports = createSelector(
  [userContext, settings],
  (userContext, settings) => {
    return userContext.scope !== 'sup'
      || (settings && !settings.supervisor_permissions) || (settings && settings.supervisor_permissions.review_open_reports !== false)
  }
);

const canAssignSchedules = createSelector(
  [userContext, settings],
  (userContext, settings) => {
    return userContext.scope !== 'sup'
      || (settings && !settings.supervisor_permissions) || (settings && settings.supervisor_permissions.assign_schedules !== false)
  }
);

const canViewObservationAnalytics = createSelector(
  [userContext, settings],
  (userContext, settings) => {
    return userContext.scope !== 'sup'
      || (settings && !settings.supervisor_permissions) || (settings && settings.supervisor_permissions.observation_analytics !== false)
  }
);

const canViewLateFtoNotifications = createSelector(
  [userContext, settings],
  (userContext, settings) => {
    return userContext.scope !== 'sup'
      || (settings && !settings.supervisor_permissions) || (settings && settings.supervisor_permissions.view_late_fto_notifications !== false)
  }
);

const canViewLateOitAck = createSelector(
  [userContext, settings],
  (userContext, settings) => {
    return userContext.scope !== 'sup'
      || (settings && !settings.supervisor_permissions) || (settings && settings.supervisor_permissions.view_late_oit_acknowledgement !== false)
  }
);

const canViewTaskProgress = createSelector(
  [userContext, settings],
  (userContext, settings) => {
    return userContext.scope !== 'sup'
      || (settings && !settings.supervisor_permissions) || (settings && settings.supervisor_permissions.view_task_progress !== false)
  }
);

const canChangeFtoInSchedule = createSelector(
  [settings],
  (settings) => {
    return settings.allowChangeFtoInSchedule;
  }
);

export default {
  selectedNotification,
  oitShortLabel,
  ftoShortLabel,
  ftocShortLabel,
  supShortLabel,
  cstaffShortLabel,
  chiefShortLabel,
  oitLabel,
  ftoLabel,
  ftocLabel,
  supLabel,
  cstaffLabel,
  chiefLabel,
  currentRoleName,
  availableTaskResponses,
  canViewAnayitics,
  canReviewOpenReports,
  canAssignSchedules,
  canViewObservationAnalytics,
  canViewLateFtoNotifications,
  canViewLateOitAck,
  canViewTaskProgress,
  canChangeFtoInSchedule
};
